import { Alert, AlertDescription } from "@shared/ui/alert";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";
import { Separator } from "@shared/ui/separator";
import {
  Calendar,
  Check,
  CheckCircle,
  CreditCard,
  FileText,
  Home,
  Info,
  Receipt,
  User,
  Loader2,
} from "lucide-react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PaymentMethodCard } from "./PaymentMethodCard";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import {
  useCreatePayment,
  usePaymentMethod,
  usePaymentChannelChoices,
  usePaymentPayTypeChoices,
  useCreateNscPayment,
} from "@features/cx/payments/hooks";
import { CreatePaymentPayload } from "@features/cx/payments/types";
import { PaymentMethod } from "@features/cx/shared/types/consumerRequest";
import { toast } from "@shared/hooks/use-toast";
import { useForm } from "react-hook-form";
import {
  CustomFieldConfig,
  useCustomFieldsForm,
} from "@shared/utils/customFormMaker";
import { usePlanDetails } from "@features/cx/consumerManagement/hooks";
import {
  useCategoryFees,
  useConsumerDetail,
  useUpdateConsumerStatus,
} from "@features/cx/consumerOnboarding/hooks";

export function ApplicationSuccessMessage() {
  const currency = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const consumerData = location.state?.consumerData;
  const categoryCode = consumerData?.accountCategory;
  const returnPath = "/cx/dashboard";
  const { consumerId } = useParams<{ consumerId: string }>();
  const remoteUtilityId = Number(useRemoteUtilityId());

  // The state contains the merged data from your navigation
  const stateData = useLocation().state || {};

  // Support both number and object for planSelection
  const planSelectionId =
    typeof stateData.planSelection === "object" &&
      stateData.planSelection !== null
      ? stateData.planSelection.id
      : stateData.planSelection;
  const { data: planDetails } = usePlanDetails(
    remoteUtilityId,
    planSelectionId
  );

  const { data: consumerInfo } = useConsumerDetail(consumerId);
  const { data: categoryFeesData } = useCategoryFees(
    categoryCode ?? consumerInfo?.result?.category
  );

  console.log(" feesDetail", categoryFeesData);
  // Hardcoded data
  const totalAmount =
    categoryFeesData?.result?.[0]?.fees?.reduce(
      (sum: number, fee: any) => sum + (Number(fee.amount) || 0),
      0
    ) ?? 0;

  const totalDue =
    categoryFeesData?.result?.[0]?.fees?.reduce(
      (sum: number, fee: any) => sum + (Number(fee.amount) || 0),
      0
    ) ?? 0;
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentOption, setPaymentOption] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);
  const [createCreditNote, setCreateCreditNote] = useState<boolean>(false);

  // Payment API Hooks
  const createPaymentMutation = useCreateNscPayment(
    remoteUtilityId || 0,
    Number(consumerId) || 0,
    stateData?.consumerNo || ""
  );

  const paymentMethodQuery = usePaymentMethod({
    remoteUtilityId: remoteUtilityId || 0,
  });

  const paymentChannelChoicesQuery = usePaymentChannelChoices(
    remoteUtilityId || 0
  );

  const paymentPayTypeChoicesQuery = usePaymentPayTypeChoices(
    remoteUtilityId || 0
  );

  const updateConsumerStatusMutation = useUpdateConsumerStatus();

  useEffect(() => {
    if (categoryFeesData?.result?.[0]?.fees?.length === 0) {
      updateConsumerStatusMutation.mutate({
        consumerId: Number(consumerId),
        payload: { status: 16 }
      });
    }
  }, [categoryFeesData, consumerId]);

  // Get data from API
  const paymentMethods = paymentMethodQuery.data?.result
    ? Array.isArray(paymentMethodQuery.data.result)
      ? paymentMethodQuery.data.result
      : [paymentMethodQuery.data.result]
    : [];

  const paymentChannels = paymentChannelChoicesQuery.data?.result
    ? Array.isArray(paymentChannelChoicesQuery.data.result)
      ? paymentChannelChoicesQuery.data.result
      : [paymentChannelChoicesQuery.data.result]
    : [];

  const paymentPayTypes = paymentPayTypeChoicesQuery.data?.result
    ? Array.isArray(paymentPayTypeChoicesQuery.data.result)
      ? paymentPayTypeChoicesQuery.data.result
      : [paymentPayTypeChoicesQuery.data.result]
    : [];

  // Custom field configuration for payment methods - memoized to prevent re-creation
  const customFieldConfig = useMemo<CustomFieldConfig>(
    () => ({
      namingStrategy: "key-fallback",
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
      gridColumns: 3,
      responsiveColumns: { sm: 1, md: 2, lg: 3 },
      fullWidth: true,
      enableTooltips: true,
      fieldNameCleaning: true,
    }),
    []
  );

  // Create custom fields form for payment method
  const customFieldsForm = useForm({
    mode: "onBlur",
  });

  // Use the utility hook for payment method custom fields
  const {
    formFields: paymentMethodCustomFields,
    defaultValues: paymentMethodDefaults,
    resetFormWithDefaults,
    hasFields: hasPaymentMethodFields,
  } = useCustomFieldsForm(
    Array.isArray(selectedPaymentMethod?.customFields)
      ? selectedPaymentMethod.customFields
      : undefined,
    customFieldsForm,
    customFieldConfig
  );
  console.log(
    "customFieldNames",
    JSON.stringify(paymentMethodCustomFields, null, 2)
  );

  // Reset custom fields form when payment method changes
  useEffect(() => {
    if (
      selectedPaymentMethod &&
      Array.isArray(selectedPaymentMethod.customFields)
    ) {
      resetFormWithDefaults();
    }
  }, [selectedPaymentMethod?.code, resetFormWithDefaults]);

  const [paymentDetails, setPaymentDetails] = useState({
    amount: "",
    cashReceived: "",
    date: new Date().toISOString().split("T")[0],
    referenceNumber: "",
    chequeNumber: "",
    bankName: "",
    chequeDate: "",
    accountHolderName: "",
    bankAccountNumber: "",
    routingNumber: "",
    chequeAmount: "",
  });

  // Computed values
  const payNowSelected = paymentOption === "pay-now";
  const paidAmount = parseFloat(
    paymentDetails.amount ||
    paymentDetails.cashReceived ||
    paymentDetails.chequeAmount ||
    "0"
  );
  const balance = {
    paidAmount: paidAmount,
    outstanding: Math.max(0, totalDue - paidAmount),
    changeBack: Math.max(0, paidAmount - totalDue),
  };

  // Updated calculation functions to use balanceAmount instead of outstanding balance
  const getOutstandingAmount = () => {
    return Math.max(0, totalDue - (Number(paymentDetails.amount) || 0));
  };

  const getExcessAmount = () => {
    return Math.max(0, (Number(paymentDetails.amount) || 0) - totalDue);
  };

  const getChangeAmount = () => {
    if (
      selectedPaymentMethod?.name.toLowerCase() === "cash" &&
      paymentDetails.cashReceived
    ) {
      const receivedCash = parseFloat(paymentDetails.cashReceived) || 0;
      return Math.max(0, receivedCash - (Number(paymentDetails.amount) || 0));
    }
    return 0;
  };

  // Event handlers
  const handlePaymentOptionChange = (value: string) => {
    setPaymentOption(value);
    setSelectedPaymentMethod(null);
  };

  const handlePaymentMethodSelect = (method: PaymentMethod) => {
    setSelectedPaymentMethod(method);
  };

  const handleCompletePayment = async () => {
    if (!consumerId) {
      toast({
        title: "Consumer ID not found",
        variant: "destructive",
      });
      return;
    }

    try {
      // Collect custom fields data
      let customFieldsData = {};
      if (hasPaymentMethodFields && selectedPaymentMethod) {
        const customFieldNames = paymentMethodCustomFields.map(
          (field) => field.name
        );
        customFieldNames.forEach((fieldName) => {
          const value = customFieldsForm.getValues(fieldName);
          if (value !== undefined) {
            customFieldsData[fieldName] = value;
          }
        });
      }

      // Prepare payment payload
      const paymentPayload: CreatePaymentPayload = {
        remoteUtilityId: remoteUtilityId,
        consumer: Number(consumerId),
        amount: totalAmount,
        paymentPayType: 4, // Registration payment type
        paymentMode: selectedPaymentMethod?.code || "",
        paymentChannel: "walk-in", // Default for registration
        paymentDate: customFieldsData?.paymentDate ?? new Date().toISOString(),
        status: "CREDIT",
        source: 1, // Default value
        paymentReceivedStatus: 0, // Default value
        createCreditNote: createCreditNote,
        extraData: {
          additionalNotes: "Registration payment",
          billAmount: totalAmount,
          paymentAmount: paidAmount,
          excessRefund: getChangeAmount(),
          ...customFieldsData,
        },
      };
      // Create payment
      await createPaymentMutation.mutateAsync(paymentPayload);
      setPaymentCompleted(true);
    } catch (error) {
      console.error("Payment creation failed:", error);
      toast({
        title: "Failed to process payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmPaymentSelection = () => {
    if (paymentOption === "pay-now") {
      // Stay on current state to select payment method
    } else {
      setPaymentCompleted(true);
    }
  };

  const handleAddNewConsumer = () => {
    navigate("/cx/consumer-management/create/manual"); //
  };
  const handleDashboardNavigation = () => {
    navigate("/cx/consumer-onboarding");
  };
  useEffect(() => {
    paymentMethodCustomFields.forEach((field) => {
      if (field.type === "datepicker") {
        const currentValue = customFieldsForm.getValues(field.name);

        if (!currentValue) {
          const today = new Date().toISOString().split("T")[0];
          customFieldsForm.setValue(field.name, today);
        }
      }
    });
  }, [paymentMethodCustomFields, customFieldsForm]);

  // If total due is zero, show success without payment options
  if (totalAmount === 0) {
    return (
      <div>
        <Card className="border-green-200">
          <CardHeader className="bg-green-50 border-b border-green-100">
            <div className="flex justify-center mb-2">
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-center text-green-700">
              Application Successfully Submitted
            </CardTitle>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            <div className="text-center">
              <p className="text-slate-600">
                Your application has been submitted and a new consumer account
                has been created.
              </p>
              <div className="mt-2">
                <span className="bg-green-50 text-green-700 text-sm px-2 py-1 rounded-full">
                  Consumer NO:{" "}
                  {stateData?.consumerNo || consumerInfo?.result?.consumerNo || "NA"}
                </span>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-md">
              {categoryFeesData?.result?.[0]?.fees?.map((fee: any) => (
                <div key={fee.code} className="flex justify-between mb-2">
                  <span className="text-slate-600">{fee.name}</span>
                  <span className="font-medium">${fee.amount}</span>
                </div>
              ))}
              <Separator className="my-2" />
              <div className="flex justify-between font-medium">
                <span>Total Due</span>
                <span className="text-lg">
                  {currency} {totalAmount.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="text-center p-6 bg-blue-50 border border-blue-200 rounded-md">
              <CheckCircle className="h-12 w-12 text-blue-600 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-blue-700 mb-2">
                No Payment Required
              </h3>
              <p className="text-blue-600">
                Your application has been completed successfully. No fees are
                required for this plan.
              </p>
              <Button className="mt-2" onClick={handleDashboardNavigation}>Return to Dashboard</Button>
            </div>
          </CardContent>


        </Card>
      </div>
    );
  }

  return (
    <div>
      <Card className="border-green-200">
        <CardHeader className="bg-green-50 border-b border-green-100">
          <div className="flex justify-center ">
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <CardTitle className="text-center text-green-700 p-4">
            Application Successfully Submitted
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          <div className="text-center">
            <p className="text-slate-600">
              Your application has been submitted and a new consumer account has
              been created.
            </p>
            <div className="mt-2">
              <span className="bg-green-50 text-green-700 text-sm px-2 py-1 rounded-full">
                Account Number: {consumerInfo?.result?.consumerNo}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-md">
            {categoryFeesData?.result?.[0]?.fees?.map((fee: any) => (
              <div key={fee.code} className="flex justify-between mb-2">
                <span className="text-slate-600">{fee.name}</span>
                <span className="font-medium">
                  {currency} {fee.amount}
                </span>
              </div>
            ))}
            <Separator className="my-2" />
            <div className="flex justify-between font-medium">
              <span>Total Due</span>
              <span className="text-lg">
                {currency} {totalAmount.toFixed(2)}
              </span>
            </div>
          </div>

          {!paymentCompleted && (
            <>
              <div className="space-y-3">
                <h3 className="text-lg font-medium">Payment Options</h3>
                <RadioGroup
                  value={paymentOption}
                  onValueChange={handlePaymentOptionChange}
                  className="grid gap-4 grid-cols-1 md:grid-cols-2"
                >
                  <div>
                    <RadioGroupItem
                      value="pay-now"
                      id="pay-now"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="pay-now"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-slate-50 p-4 hover:bg-slate-100 hover:border-primary peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    >
                      <div className="mb-2">
                        <CreditCard className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">Pay Now</div>
                        <div className="text-sm text-slate-600">
                          Process payment immediately
                        </div>
                      </div>
                    </Label>
                  </div>

                  <div>
                    <RadioGroupItem
                      value="pay-later"
                      id="pay-later"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="pay-later"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-slate-50 p-4 hover:bg-slate-100 hover:border-primary peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    >
                      <div className="mb-2">
                        <Calendar className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">Pay Later</div>
                        <div className="text-sm text-slate-600">
                          Process payment Later
                        </div>
                      </div>
                    </Label>
                  </div>

                  {/* <div>
                    <RadioGroupItem
                      value="add-to-bill"
                      id="add-to-bill"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="add-to-bill"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-slate-50 p-4 hover:bg-slate-100 hover:border-primary peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    >
                      <div className="mb-2">
                        <Receipt className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">Add to First Bill</div>
                        <div className="text-sm text-slate-600">
                          Include fees in first invoice
                        </div>
                      </div>
                    </Label>
                  </div> */}
                  {/* 
                  <div>
                    <RadioGroupItem
                      value="send-invoice"
                      id="send-invoice"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="send-invoice"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-slate-50 p-4 hover:bg-slate-100 hover:border-primary peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    >
                      <div className="mb-2">
                        <FileText className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">Send Invoice</div>
                        <div className="text-sm text-slate-600">
                          Email invoice to consumer
                        </div>
                      </div>
                    </Label>
                  </div> */}
                </RadioGroup>
              </div>

              {paymentOption === "pay-now" && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Select Payment Mode</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {paymentMethods.map((method: PaymentMethod) => (
                      <PaymentMethodCard
                        key={method.code}
                        method={method.name}
                        value={method.code}
                        icon="card"
                        selected={selectedPaymentMethod?.code === method.code}
                        onClick={() => handlePaymentMethodSelect(method)}
                      />
                    ))}
                  </div>

                  {/* Payment method specific fields */}
                  {selectedPaymentMethod && (
                    <div className="space-y-4 bg-gray-50 p-4 rounded-md">
                      <h4 className="font-medium">
                        {selectedPaymentMethod.name} Payment Details
                      </h4>

                      {/* Custom fields for payment method */}
                      {/* {hasPaymentMethodFields && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {paymentMethodCustomFields.map((field) => (
                            <div key={field.name} className="space-y-2">
                              <Label htmlFor={field.name}>{field.label}</Label>
                              <Input
                                id={field.name}
                                type={field.type === 'decimal' ? 'number' : 'text'}
                                step={field.type === 'decimal' ? '0.01' : undefined}
                                value={customFieldsForm.watch(field.name) || ''}
                                onChange={(e) => {
                                  customFieldsForm.setValue(field.name, e.target.value);
                                  if (field.name.toLowerCase().includes('amount')) {
                                    setPaymentDetails((prev) => ({
                                      ...prev,
                                      amount: e.target.value,
                                    }));
                                  }
                                }}
                                placeholder={field.placeholder || ''}
                                disabled={field.disabled}
                              />
                            </div>
                          ))}
                        </div>
                      )} */}

                      {hasPaymentMethodFields && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {paymentMethodCustomFields.map((field) => {
                            let inputType: "text" | "number" | "date" = "text";

                            if (field.type === "decimal") {
                              inputType = "number";
                            } else if (field.type === "datepicker") {
                              inputType = "date";
                            }

                            return (
                              <div key={field.name} className="space-y-2">
                                <Label htmlFor={field.name}>
                                  {field.label}
                                </Label>
                                <Input
                                  id={field.name}
                                  type={inputType}
                                  step={
                                    field.type === "decimal"
                                      ? "0.01"
                                      : undefined
                                  }
                                  value={
                                    (field.name === "amount" &&
                                      ["Cash", "Online"].includes(
                                        selectedPaymentMethod.name
                                      )) ||
                                      (field.name === "payment_amount" &&
                                        selectedPaymentMethod.name !== "Cash")
                                      ? totalDue
                                      : customFieldsForm.watch(field.name) ?? ""
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    customFieldsForm.setValue(
                                      field.name,
                                      value
                                    );

                                    if (
                                      field.name
                                        .toLowerCase()
                                        .includes("amount")
                                    ) {
                                      setPaymentDetails((prev) => ({
                                        ...prev,
                                        amount: value,
                                      }));
                                    }
                                  }}
                                  placeholder={field.placeholder || ""}
                                  disabled={
                                    field.name === "amount" &&
                                    ["Cash", "Online"].includes(
                                      selectedPaymentMethod.name
                                    ) ||
                                    (field.name === "payment_amount" &&
                                      selectedPaymentMethod.name !== "Cash")
                                    ||
                                    field.disabled}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Balance summary */}
                      {paymentDetails?.amount && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="text-sm">
                            <div className="flex justify-between mb-1">
                              <span>Total Due:</span>
                              <span className="font-medium">
                                {currency} {totalDue?.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between mb-1">
                              <span>Amount Paid:</span>
                              <span className="font-medium">
                                {currency} {balance?.paidAmount.toFixed(2)}
                              </span>
                            </div>
                            {balance.outstanding > 0 && (
                              <div className="flex justify-between text-red-600 font-medium">
                                <span>Outstanding Balance:</span>
                                <span>
                                  {currency} {balance?.outstanding.toFixed(2)}
                                </span>
                              </div>
                            )}
                            {balance.changeBack > 0 && (
                              <div className="flex justify-between text-green-600 font-medium">
                                <span>Change to Return:</span>
                                <span>
                                  {currency} {balance?.changeBack.toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Credit note option for excess amount */}
                      {getChangeAmount() > 0 && (
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="createCreditNote"
                              checked={createCreditNote}
                              onChange={(e) =>
                                setCreateCreditNote(e.target.checked)
                              }
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor="createCreditNote"
                              className="text-sm font-medium text-green-800"
                            >
                              Create a credit note for change amount
                            </label>
                          </div>
                          <p className="text-xs text-green-700 mt-1 ml-6">
                            A credit note will be created for {currency}
                            {getChangeAmount()} that can be applied to future
                            bills
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {paymentOption && (
                <Alert className="bg-blue-50 border border-blue-200">
                  <Info className="h-4 w-4 text-blue-500" />
                  <AlertDescription className="text-blue-700">
                    {paymentOption === "pay-now" &&
                      !selectedPaymentMethod &&
                      "Please select a payment method to proceed."}
                    {paymentOption === "pay-now" &&
                      selectedPaymentMethod &&
                      "Please fill in all payment details to complete your application."}
                    {paymentOption === "pay-later" &&
                      "You'll receive a payment link via email. Please complete payment within 7 days."}
                    {paymentOption === "add-to-bill" &&
                      "All fees will be included in your first monthly bill. Service will be activated immediately."}
                    {paymentOption === "send-invoice" &&
                      "An invoice will be sent to the email address provided. Payment terms: Net 30."}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {paymentCompleted && (
            <div className="space-y-4">
              <div className="text-center p-6 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-green-700 mb-2">
                  Payment Completed Successfully!
                </h3>
                <p className="text-green-600">
                  Your payment has been processed and your consumer application
                  is now complete.
                </p>
                {balance?.changeBack > 0 && (
                  <div className="mt-3 p-3 bg-green-100 rounded-md">
                    <p className="text-green-700 font-medium">
                      Change to return: {currency}{" "}
                      {balance?.changeBack?.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-wrap gap-3 justify-between  border-t pt-4">
          {!paymentCompleted ? (
            <div className="space-x-2">
              {payNowSelected && selectedPaymentMethod ? (
                <Button
                  onClick={handleCompletePayment}
                  disabled={createPaymentMutation.isPending}
                  className="bg-primary hover:bg-primary/90"
                >
                  {createPaymentMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Complete Payment
                    </>
                  )}
                </Button>
              ) : payNowSelected ? (
                <Button disabled className="bg-gray-400">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Select Payment Method
                </Button>
              ) : (
                <Button
                  onClick={handleConfirmPaymentSelection}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Check className="mr-2 h-4 w-4" />
                  Confirm Selection
                </Button>
              )}
            </div>
          ) : (
            <div className="space-x-2">

            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
